import { Component } from '@theme/component';

/**
 * DAVIDsTEA share control.
 *
 * Hands off to the operating system's share sheet where one exists, which is what phones
 * and most tablets want, and falls back to copying the URL on desktop. The label swaps to
 * a confirmation for a moment either way, because copying gives no feedback of its own.
 *
 * Cancelling the native share sheet throws AbortError. That is a normal outcome, not a
 * failure, so it must not fall through to copying — otherwise dismissing the sheet would
 * silently put the link on the clipboard.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} label
 *
 * @extends {Component<Refs>}
 */
export class DtShareButton extends Component {
  requiredRefs = ['label'];

  /** @type {number | undefined} */
  #timer;

  disconnectedCallback() {
    if (this.#timer) clearTimeout(this.#timer);
    super.disconnectedCallback();
  }

  async share() {
    const url = this.dataset.url || window.location.href;
    const title = this.dataset.title || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#confirm();
    } catch {
      // Clipboard access can be refused outright. Saying nothing beats a false confirmation.
    }
  }

  #confirm() {
    const { label } = this.refs;
    const original = this.dataset.labelDefault ?? label.textContent ?? '';

    label.textContent = this.dataset.labelCopied ?? original;

    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
}

if (!customElements.get('dt-share-button')) {
  customElements.define('dt-share-button', DtShareButton);
}
